// ---------------------------------------------------------------------------
// OAuth Provider Definitions — token URLs, scopes, and client config
// per integration that supports OAuth2 authorization code flow.
// ---------------------------------------------------------------------------

export interface OAuthProviderConfig {
  /** Integration definition ID */
  definitionId: string;
  /** OAuth2 authorization endpoint */
  authorizeUrl: string;
  /** OAuth2 token endpoint */
  tokenUrl: string;
  /** Optional token revocation endpoint */
  revokeUrl?: string;
  /**
   * Optional grant-revocation endpoint used to FORCE a fresh consent screen on
   * reconnect. Distinct from `revokeUrl` (which revokes a single token): this
   * revokes the user's entire authorization *grant* for the OAuth app, so the
   * next /authorize redirect cannot be silently short-circuited.
   *
   * GitHub is the motivating case (ENG-6187). GitHub does NOT honour
   * `prompt=consent` — once a user has authorized an OAuth App, the authorize
   * endpoint redirects straight back carrying the *previously granted* (and
   * possibly narrower) scope set. A reconnect therefore can never widen scopes:
   * the user keeps landing on the same missing-scopes banner in a loop. Revoking
   * the grant before redirecting forces GitHub to re-display consent so the new
   * scopes are actually granted.
   *
   * `{client_id}` is substituted with the resolved client id at call time. The
   * call requires HTTP Basic auth (client_id:client_secret) and the user's
   * access_token in the request body. It is best-effort — a failed revoke must
   * never block the reconnect (the worst case is the pre-fix behaviour).
   */
  grantRevokeUrl?: string;
  /** Default scopes to request */
  defaultScopes: string[];
  /**
   * Optional read-only scope set. When the /authorize caller passes
   * `read_only: true`, these scopes are requested instead of `defaultScopes`
   * (and the ENG-4956 agent-required-scope union is skipped — read-only is an
   * explicit operator choice that deliberately declines write access). Providers
   * without this field reject a read_only request.
   *
   * ENG-7897: `credentials.read_only` on the install is the source of truth for
   * WHETHER an install is read-only; this list only governs which scopes that
   * mode requests. The two were previously conflated — the mode was inferred
   * from the granted scopes, which cannot distinguish a read-only install from
   * a write install whose grant came back short. `credentials.granted_scopes`
   * still records what the vendor actually handed back, and the drift check
   * diffs it against whichever set this mode resolves to today.
   */
  readOnlyScopes?: string[];
  /** Whether the provider supports refresh tokens */
  supportsRefresh: boolean;
  /** Additional params to include in the authorize URL */
  extraAuthorizeParams?: Record<string, string>;
  /** How to send client credentials in token exchange ('body' or 'basic') */
  clientAuthMethod: 'body' | 'basic';
  /** Provider-specific function to extract user info from tokens for status_message */
  userInfoUrl?: string;
  /**
   * PKCE method. Set to 'S256' for providers that mandate (or recommend) PKCE.
   * When set, the shared /authorize route generates a code_verifier, stores it
   * with the OAuth state row, and sends code_challenge + code_challenge_method
   * on the authorize URL. The /callback route retrieves the verifier from
   * state and includes it in the token exchange. Public clients (token_endpoint_auth_method: none)
   * with PKCE skip the client_secret on the token exchange.
   */
  pkce?: 'S256';
  /**
   * Whether the OAuth client can authenticate without a client_secret (RFC 6749
   * "public client", typically combined with PKCE). When true, the token
   * exchange POST omits client_secret and only sends client_id. Defaults to
   * false (confidential client; client_secret required).
   */
  publicClient?: boolean;
  /**
   * Remote streamable-HTTP MCP endpoint hosted by the provider. When set, the
   * Claude Code provisioner emits a `.mcp.json` entry pointing at this URL
   * with an `Authorization: Bearer ${ACCESS_TOKEN}` header sourced from the
   * integration's credentials. Lets new remote-MCP integrations ride the
   * shared OAuth registry + refresh path instead of carrying hand-rolled
   * blocks in `buildMcpJson`.
   */
  mcpUrl?: string;
  /**
   * Curated allowlist of tool names this remote MCP should expose to the agent
   * (ENG-6948). Remote MCP servers can advertise a far larger surface than the
   * catalog curates (Kajabi advertises 111 tools; the catalog curates 25), and
   * a direct/proxied remote MCP is otherwise all-or-nothing. When set, the
   * remote-oauth-proxy filters `tools/list` to these names and rejects
   * `tools/call` for anything outside the set (see AGT_REMOTE_MCP_TOOL_ALLOWLIST
   * in `remote-oauth-proxy.ts`). Must stay in sync with the catalog seed's
   * `defined_scopes[].tools` for this definition - the drift-guard test in
   * `__tests__/oauth-provider-tool-allowlist.test.ts` enforces that. Unset =
   * no filtering (full pass-through), the default for every other provider.
   */
  toolAllowlist?: readonly string[];

  /**
   * Remote "toolsets" to activate once at session start, before the harness's
   * connect-time `tools/list` is answered (CS-1446). Some remotes (Kajabi) gate
   * whole tool groups behind a runtime `enable_toolset` call and do NOT
   * advertise those tools in `tools/list` until the group is active. Claude Code
   * freezes its callable tool manifest from that first `tools/list` and never
   * re-lists mid-session, so a group activated later (by the agent calling
   * enable_toolset itself) is reported under `newly_available_tools` but never
   * becomes callable. Listing the groups here makes the proxy pre-activate them
   * (via AGT_REMOTE_MCP_PREENABLE_TOOLSETS) so their allowlisted tools are
   * advertised in the frozen manifest. Idempotent; the toolAllowlist still caps
   * what is actually callable. Unset = no pre-enable (default).
   */
  preEnableToolsets?: readonly string[];

  /**
   * ENG-8512: reject a `tools/call` whose argument we KNOW the remote will
   * accept and silently ignore.
   *
   * The motivating case: Kajabi's `update_course` takes a `thumbnail`, which
   * stores a relative path into Kajabi's own storage. Pass an external URL and
   * the API returns **HTTP 200 and echoes the URL back** — while ingesting
   * nothing and rendering a broken image. That is worse than an error, because
   * every layer above records success: the agent marks the card done and reports
   * completion to the customer. It has already produced a false "done" to a
   * client and three failed cards nobody root-caused.
   *
   * We cannot stop the remote returning 200 — `update_course` is Kajabi's tool,
   * not ours, and its schema comes from their `tools/list` at runtime. But every
   * call traverses our proxy, so we can refuse to forward one we can prove is
   * futile, with an error naming what the field actually expects.
   *
   * Deliberately conservative. A rule belongs here only when the value is
   * *provably* wrong for the field — never as a guess at what an API might
   * dislike. A false rejection blocks real work; the silent-200 it replaces only
   * wastes it.
   *
   * Per-provider (delivered via AGT_REMOTE_MCP_ARG_REJECTS) rather than a global
   * table keyed by tool name, because tool names are not namespaced across
   * remotes — another provider's `update_course` must not inherit Kajabi's rule.
   * Unset = no argument checking (the default for every other provider).
   */
  argRejects?: readonly RemoteMcpArgRejectRule[];
}

/**
 * One ENG-8512 argument rule: on `tool`, a value for `field` matching `pattern`
 * is refused before the call is forwarded, and `message` tells the caller what
 * the field actually wants.
 */
export interface RemoteMcpArgRejectRule {
  /** Remote tool name, e.g. `update_course`. */
  tool: string;
  /** Top-level argument key, e.g. `thumbnail`. */
  field: string;
  /**
   * JS regex source, tested against the argument value when it is a **scalar**
   * - a string, a number, or a boolean - after `String(value)`.
   *
   * Objects, arrays, null and undefined are skipped rather than coerced:
   * `String({})` is "[object Object]" and `String([1,2])` is "1,2", either of
   * which a loose pattern could match and wrongly refuse a valid structured
   * argument. That hazard is the reason for the skip and it applies only to
   * non-scalars - `String(42)` is "42" and `String(true)` is "true", both
   * faithful.
   *
   * CS-1554: numbers and booleans used to be skipped alongside objects, which
   * silently capped this whole guard to string-valued fields. The first real
   * case that needed it was an integer (`search_contacts.tag_id`), and the rule
   * would have parsed, loaded, matched nothing and protected nobody - a guard
   * against silent no-ops, silently doing nothing.
   */
  pattern: string;
  /** Caller-facing explanation. Should say what a VALID value looks like. */
  message: string;
}

export const OAUTH_PROVIDERS: Record<string, OAuthProviderConfig> = {
  'google-workspace': {
    definitionId: 'google-workspace',
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    tokenUrl: 'https://oauth2.googleapis.com/token',
    revokeUrl: 'https://oauth2.googleapis.com/revoke',
    defaultScopes: [
      'https://www.googleapis.com/auth/gmail.modify',
      'https://www.googleapis.com/auth/calendar',
      'https://www.googleapis.com/auth/drive',
      'https://www.googleapis.com/auth/spreadsheets',
      'https://www.googleapis.com/auth/documents',
      'https://www.googleapis.com/auth/chat.messages',
      'https://www.googleapis.com/auth/chat.spaces.readonly',
    ],
    supportsRefresh: true,
    extraAuthorizeParams: {
      access_type: 'offline',
      prompt: 'consent',
    },
    clientAuthMethod: 'body',
    userInfoUrl: 'https://www.googleapis.com/oauth2/v2/userinfo',
  },

  'github': {
    definitionId: 'github',
    authorizeUrl: 'https://github.com/login/oauth/authorize',
    tokenUrl: 'https://github.com/login/oauth/access_token',
    // ENG-6187: revoke the existing grant on reconnect so GitHub re-prompts and
    // the four scopes below are actually granted. Without this, a stale narrow
    // grant (e.g. an old read:user-only authorization) is silently re-issued and
    // the missing-scopes banner loops forever.
    grantRevokeUrl: 'https://api.github.com/applications/{client_id}/grant',
    defaultScopes: ['repo', 'read:org', 'gist', 'workflow'],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'body',
    userInfoUrl: 'https://api.github.com/user',
  },

  'granola': {
    // Granola MCP — remote streamable-HTTP at https://mcp.granola.ai/mcp.
    // The AS is at mcp-auth.granola.ai and exposes RFC 8414 metadata at
    // /.well-known/oauth-authorization-server. Auth is OAuth 2.0 with
    // mandatory PKCE (S256) and a public client (no client_secret) issued
    // via Dynamic Client Registration (RFC 7591). The bootstrap script
    // (`packages/api/scripts/dcr-register.ts`) registers a client once at
    // deploy time; OAUTH_GRANOLA_CLIENT_ID is set from its output.
    definitionId: 'granola',
    authorizeUrl: 'https://mcp-auth.granola.ai/oauth2/authorize',
    tokenUrl: 'https://mcp-auth.granola.ai/oauth2/token',
    // Minimal scope set: `offline_access` earns the refresh_token so the
    // refresh cron can rotate the bearer without operator action; `openid`
    // is required for the OIDC code flow even when we don't request an
    // id_token. Profile/email are intentionally omitted — we have no
    // userInfoUrl wired up here, so requesting them would over-ask consent
    // for fields the callback can't read.
    defaultScopes: ['openid', 'offline_access'],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'body',
    pkce: 'S256',
    publicClient: true,
    mcpUrl: 'https://mcp.granola.ai/mcp',
    // Curated surface (matches the catalog seed's defined_scopes[].tools).
    toolAllowlist: ['search-meetings', 'read-transcript', 'read-summary', 'list-folders'],
  },

  'brand-ninja': {
    // ENG-6820: Brand Ninja External-Content MCP, remote streamable-HTTP at
    // https://ext-api.app.brandninja.ai/v1/mcp. Same shape as Granola: the
    // server exposes RFC 8414 authorization-server metadata at
    // /.well-known/oauth-authorization-server (values below are taken verbatim
    // from that document, not inferred). Auth is OAuth 2.0 authorization-code
    // with mandatory PKCE (S256) and a public client (token_endpoint_auth_method
    // 'none') issued via Dynamic Client Registration (RFC 7591). The bootstrap
    // script (packages/api/scripts/dcr-register.ts) registers a client once at
    // deploy time against the registration_endpoint
    // (https://ext-api.app.brandninja.ai/v1/oauth/register); OAUTH_BRAND_NINJA_CLIENT_ID
    // is set from its output. The AS advertises the refresh_token grant, so the
    // shared oauth-refresh cron rotates the bearer without operator action.
    definitionId: 'brand-ninja',
    authorizeUrl: 'https://prod-brandninja.auth.ap-southeast-2.amazoncognito.com/oauth2/authorize',
    tokenUrl: 'https://prod-brandninja.auth.ap-southeast-2.amazoncognito.com/oauth2/token',
    // The resource server (ext-api.app.brandninja.ai) advertises exactly two
    // scopes: external-api/content.write (the default content surface) and
    // external-api/admin (read-only credential metadata, granted per account
    // admin). Default install is least-privilege: content.write only; an
    // operator can widen to admin out of band. No openid/offline_access in the
    // advertised scope set, so Cognito issues the refresh_token for the code grant
    // regardless, so requesting only the resource scope keeps consent minimal.
    defaultScopes: ['external-api/content.write'],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'body',
    pkce: 'S256',
    publicClient: true,
    mcpUrl: 'https://ext-api.app.brandninja.ai/v1/mcp',
    // Curated surface (matches the catalog seed's defined_scopes[].tools).
    toolAllowlist: [
      'submit_content_request', 'get_content_status', 'list_content_requests',
      'list_channels', 'list_brands', 'list_topics', 'list_timeline_templates',
      'list_content_types', 'list_source_skills', 'list_output_skills', 'list_conversion_skills',
      'list_credentials', 'search_transcripts', 'create_source_clips', 'create_timeline_ranking',
      'create_topic', 'create_timeline_template', 'create_knowledge', 'link_knowledge',
    ],
  },

  'kajabi': {
    // Kajabi MCP — remote streamable-HTTP at https://mcp.kajabi.com/mcp.
    // Same Granola/Brand-Ninja shape: OAuth 2.0 authorization-code with
    // mandatory PKCE (S256) and a public client (token_endpoint_auth_method
    // 'none') issued via Dynamic Client Registration (RFC 7591). Values below
    // are taken verbatim from Kajabi's RFC 8414 metadata at
    // https://mcp.kajabi.com/.well-known/oauth-authorization-server (a Rails
    // Doorkeeper AS), not inferred. NOTE the authorize host differs from the
    // token host: authorize is on app.kajabi.com (the login surface), while
    // token/register/revoke are on mcp.kajabi.com — do NOT "normalise" them to
    // one host. The bootstrap script (packages/api/scripts/dcr-register.ts)
    // registers a client once at deploy time against
    // https://mcp.kajabi.com/mcp/oauth/register; OAUTH_KAJABI_CLIENT_ID is set
    // from its output. Doorkeeper RESTRICTS a dynamic client to the scopes it
    // registered with, so register with at least the union of defaultScopes
    // below (--scope 'read write:contacts write:emails write:content
    // write:commerce'). Widening defaultScopes forces a client re-register
    // (new client_id) AND a per-connection re-consent: existing tokens keep
    // the old scope set and their refreshes fail under the new client_id, so
    // each connection flips to needs_reauth until the user re-runs Connect
    // (ENG-7483). The AS advertises the refresh_token grant (no
    // openid/offline_access scope needed), so the shared oauth-refresh cron
    // rotates the bearer without operator action.
    definitionId: 'kajabi',
    authorizeUrl: 'https://app.kajabi.com/mcp/oauth/authorize',
    tokenUrl: 'https://mcp.kajabi.com/mcp/oauth/token',
    revokeUrl: 'https://mcp.kajabi.com/mcp/oauth/revoke',
    // Coarse Doorkeeper scopes (NOT openid-style). Cross-domain reads (`read`)
    // plus the write surfaces this integration ships: contact tags/segments,
    // email broadcasts/sequences, and course updates (write:content gates
    // update_course, the course-thumbnail path, ENG-7483). write:commerce is
    // requested now (Brad's call on ENG-7483) so a later offer/pricing-write
    // enablement needs no extra re-consent round; the tool allowlist below
    // still exposes no commerce write, so the callable surface stays minimal.
    defaultScopes: ['read', 'write:contacts', 'write:emails', 'write:content', 'write:commerce'],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'body',
    pkce: 'S256',
    publicClient: true,
    mcpUrl: 'https://mcp.kajabi.com/mcp',
    // Curated surface (matches the catalog seed's defined_scopes[].tools). Kajabi's
    // live MCP advertises ~111 tools; the agent only needs these 34.
    // (Was written as 28 and left stale when the four pages/themes tools landed
    // in ENG-7629's follow-up — recounted here rather than left to drift again.)
    // CS-1427: `enable_toolset` is included so an agent can activate its own
    // products/commerce + analytics toolsets on the connection (Kajabi gates those
    // tool groups behind a runtime toolset that returns "not active" until enabled).
    // Low-risk: this proxy still caps every tools/call to the allowlist, so a broad
    // activation can't widen what's actually callable.
    // ENG-7483: `get_course`/`update_course` enable the course-thumbnail refresh
    // (update_course covers title/description/thumbnail per Kajabi's MCP docs).
    // Fail-safe on naming drift: an allowlisted tool the server doesn't advertise
    // is logged and skipped by the proxy, never a break.
    toolAllowlist: [
      'list_sites', 'get_site_summary', 'select_site', 'search_contacts', 'get_contact',
      'search_products', 'get_product', 'list_offers', 'get_offer', 'list_offer_purchases',
      'get_revenue_analytics', 'get_contacts_analytics', 'list_tags', 'create_tag', 'tag_contact',
      'untag_contact', 'list_segments', 'create_segment', 'update_segment', 'list_broadcasts',
      'get_broadcast', 'create_broadcast', 'list_sequences', 'get_sequence', 'create_sequence',
      'enable_toolset', 'get_course', 'update_course',
      // ENG-7629 follow-up: pages (Dee's landing-page edit) + themes (CS-1448
      // theme-builder broadcast content). Callable via the proxy union manifest
      // + per-call enable; matches the new kajabi:pages catalog scope.
      'list_landing_pages', 'get_landing_page', 'update_landing_page', 'get_theme_content',
      // CS-1689: the write half of the two groups above. Every Kajabi write we
      // curate stopped at the email-content boundary — an agent could
      // create_sequence and get_theme_content but could not add an email to a
      // sequence or apply a theme edit, so any change to an EXISTING email had
      // to go back to the customer by hand.
      //
      // Both names are Kajabi's own, taken from the descriptions of tools we
      // already advertise rather than guessed: `create_sequence` says "Add
      // emails with add_sequence_email", and `create_broadcast` +
      // `get_theme_content` both say to "pair with update_theme_content to
      // apply changes". That sourcing matters here — a name the server does not
      // advertise is logged and SKIPPED by the proxy, so a wrong guess would
      // look applied and change nothing.
      //
      // No new catalog scope: these join the existing kajabi:emails and
      // kajabi:pages scopes, so already-connected sites do not need to
      // re-authorise to pick them up.
      'add_sequence_email', 'update_theme_content',
    ],
    // CS-1446: Kajabi gates several tool groups behind a runtime `enable_toolset`
    // and does not advertise their tools in `tools/list` until the group is
    // active. The harness freezes its callable-tool manifest from the
    // connect-time `tools/list`, so a group the agent activates later never
    // becomes callable (repro: enable_toolset('courses') → get_course/
    // update_course still "No such tool available"). Pre-activate the gated
    // groups whose tools we allowlist so they are advertised in that first
    // manifest. Names are Kajabi's toolset ids (courses per CS-1446;
    // products/commerce/analytics per CS-1427). Best-effort + idempotent: an
    // unknown/unscoped name is logged and skipped by the proxy, and the
    // toolAllowlist above still caps what is actually callable.
    //
    // ENG-8392: `contacts` + `emails` were missing, so thirteen allowlisted
    // tools were unreachable by construction — list_tags, create_tag,
    // tag_contact, untag_contact, list_segments, create_segment,
    // update_segment (contacts) and list_broadcasts, get_broadcast,
    // create_broadcast, list_sequences, get_sequence, create_sequence
    // (emails). A customer agent was blocked on exactly those.
    //
    // These two ids are Kajabi's OWN spelling, not ours: the server names the
    // toolset in its rejection, e.g. `list_tags` returns
    //   "The 'contacts' toolset is not active. Call enable_toolset with
    //    name: 'contacts' to activate it first."
    // and `list_sequences` returns the same with 'emails'. Sourced that way
    // deliberately — a wrong id here is logged and SKIPPED by the proxy, so a
    // guess would look applied and change nothing.
    //
    // Why this went unnoticed for four rounds: `get_contacts_analytics` lives
    // in the `analytics` group, which we already pre-enable. So the contacts
    // family read as partly alive (you could see a site's contact count and
    // growth curve) while every tag and segment call failed.
    preEnableToolsets: [
      'courses', 'products', 'commerce', 'analytics', 'pages', 'themes', 'contacts', 'emails',
    ],
    // ENG-8512: `update_course.thumbnail` stores a RELATIVE path into Kajabi's
    // own storage — it is rendered as
    //   https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/<value>
    // so an absolute http(s) URL is structurally not a valid value for it.
    // Kajabi accepts one anyway, returns 200, echoes it back, ingests nothing,
    // and the course shows a broken image. Reported from a live client site
    // (DTI) via CS-1548 after it produced a false "done" report and three
    // failed cards.
    //
    // Anchored at the start so only an ABSOLUTE URL is refused; every relative
    // path — the valid shape — passes untouched. This does not enable setting a
    // thumbnail from a URL (that needs an upload path, CS-1548 item 4). It stops
    // us reporting success for a write that did not happen.
    argRejects: [
      {
        tool: 'update_course',
        field: 'thumbnail',
        pattern: '^\\s*https?://',
        message:
          'thumbnail takes a relative path inside Kajabi storage, not an external URL. Kajabi accepts a URL here, returns 200 and never ingests it, so the image would silently stay broken. Upload the asset to Kajabi first and pass the storage path it returns.',
      },
      // CS-1554: `search_contacts` accepts `tag_id`, does not apply it, and
      // returns the FULL unfiltered contact list with `filters_applied: null`.
      // Reported first-hand from a live client site (DTI) while auditing which
      // contacts carried an opt-in tag. Corroborating evidence: Kajabi's own
      // REST reference filters contacts with `filter[has_tag_id]`, not a bare
      // `tag_id` — so the argument is dropped as unrecognised and the response
      // says so, if you read `filters_applied`.
      //
      // Worse than the thumbnail case it sits next to. A broken image is
      // visible; an unfiltered list that reads as filtered produces a confident
      // wrong ANSWER, which a human then acts on. That is the same shape as the
      // incident in CS-1554 where the agent told the customer content did not
      // exist when it did.
      //
      // Any value at all is refused: the field is not partially supported, it
      // is inert. `[\s\S]` matches every stringified scalar including 0 and
      // false, which a `.`-style pattern anchored on truthiness would miss.
      //
      // NOT REPRODUCED BY ME — I have no Kajabi connection. This rests on the
      // CS-1554 first-hand report plus the REST reference. The file's own bar is
      // "provably wrong, never a guess", so a reviewer with a live Kajabi
      // connection should confirm before this merges. If `tag_id` DOES filter
      // for some tenant, drop this rule and keep the scalar fix.
      {
        tool: 'search_contacts',
        field: 'tag_id',
        pattern: '[\\s\\S]',
        message:
          'tag_id is accepted by Kajabi and never applied — the call returns the FULL contact list with filters_applied: null, which reads as a filtered result and is not one. There is no working tag filter on this tool today. Use list_tags plus the per-contact tag data to determine membership, and treat any tag audit built on search_contacts(tag_id) as unfiltered.',
      },
    ],
  },

  'notion-cli': {
    // Notion's public OAuth app. Tokens are workspace-scoped and long-lived —
    // Notion does not issue refresh_tokens, so `supportsRefresh: false` and
    // the refresh cron skips this provider entirely. Scopes are not part of
    // Notion's authorize URL contract; consent is governed by what the user
    // grants in the OAuth screen, so `defaultScopes` stays empty.
    // `owner=user` forces the user-OAuth variant (vs internal integration).
    // Requires OAUTH_NOTION_CLI_CLIENT_ID and OAUTH_NOTION_CLI_CLIENT_SECRET.
    definitionId: 'notion-cli',
    authorizeUrl: 'https://api.notion.com/v1/oauth/authorize',
    tokenUrl: 'https://api.notion.com/v1/oauth/token',
    defaultScopes: [],
    supportsRefresh: false,
    extraAuthorizeParams: {
      owner: 'user',
    },
    clientAuthMethod: 'basic',
  },

  'xero': {
    definitionId: 'xero',
    authorizeUrl: 'https://login.xero.com/identity/connect/authorize',
    tokenUrl: 'https://identity.xero.com/connect/token',
    revokeUrl: 'https://identity.xero.com/connect/revocation',
    defaultScopes: [
      'openid',
      'profile',
      'email',
      'offline_access',
      // Granular scopes (required for apps created after March 2, 2026 —
      // do NOT revert to the broad `accounting.transactions` /
      // `accounting.contacts` scopes, Xero rejects the manifest).
      // The variant *without* `.read` is the read+write granular scope.
      'accounting.settings.read',
      // contacts: write enables agent-driven supplier/customer creation
      // (required for bill creation since a bill must reference a contact).
      'accounting.contacts',
      // invoices: write enables bill creation (Type=ACCPAY invoices) and
      // updates to sales invoices alongside the existing read access.
      'accounting.invoices',
      // attachments: write enables agents to attach the source PDF to a
      // bill at creation time. Read-only would force a follow-up manual
      // upload in Xero; write closes the loop.
      'accounting.attachments',
      // accounting.transactions.read → granular read-only replacements
      // for the surfaces we don't yet need write access on.
      'accounting.payments.read',
      'accounting.banktransactions.read',
      // manualjournals: write (CS-1439) enables the xero-broker to post
      // manual journals (reclasses, accruals, corrections) on approval. The
      // variant without `.read` is the read+write granular scope. Note: an
      // existing Xero install must RECONNECT for its token to carry this — the
      // broker posts journals with the agent's own OAuth access_token.
      'accounting.manualjournals',
      // accounting.reports.read → granular read-only replacements
      'accounting.reports.balancesheet.read',
      'accounting.reports.profitandloss.read',
      'accounting.reports.trialbalance.read',
      'accounting.reports.budgetsummary.read',
      'accounting.reports.banksummary.read',
      'accounting.reports.executivesummary.read',
      'accounting.reports.aged.read',
    ],
    // Read-only variant (ENG-6170): the `.read` granular scope for every
    // surface, so a token granted under it cannot create bills/invoices or
    // mutate contacts/attachments. Used when an install is (re)connected with
    // `read_only: true` — e.g. to lock a production-books integration to reads
    // until per-vendor broker mediation (ENG-4922) gates its writes.
    readOnlyScopes: [
      'openid',
      'profile',
      'email',
      'offline_access',
      'accounting.settings.read',
      'accounting.contacts.read',
      'accounting.invoices.read',
      'accounting.attachments.read',
      'accounting.payments.read',
      'accounting.banktransactions.read',
      'accounting.manualjournals.read',
      'accounting.reports.balancesheet.read',
      'accounting.reports.profitandloss.read',
      'accounting.reports.trialbalance.read',
      'accounting.reports.budgetsummary.read',
      'accounting.reports.banksummary.read',
      'accounting.reports.executivesummary.read',
      'accounting.reports.aged.read',
    ],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'basic',
    userInfoUrl: 'https://api.xero.com/connections',
  },

  // LinkedIn Ads (Marketing API). Confidential client with a static secret and
  // refresh tokens (~60-day access token, ~1-year rotating refresh token), so it
  // mirrors the Xero shape: clientAuthMethod 'body', supportsRefresh true, no
  // PKCE. The shared authorize/callback/refresh machinery handles the rest.
  //
  // The single shared app is Augmented Team's own LinkedIn developer app
  // (client id/secret in OAUTH_LINKEDIN_ADS_CLIENT_ID / _SECRET) - every org
  // connects its own ad accounts against it; only the per-connection tokens
  // differ. Register `${API_PUBLIC_URL}/integrations/oauth/callback` as an
  // Authorized redirect URL on the LinkedIn app.
  //
  // NOTE (linkedin-verify): the grantable scope set depends on which LinkedIn
  // API products the app is approved for (Advertising API, Lead Sync API,
  // Community Management API). Keep this list to scopes the app actually holds -
  // requesting an ungranted scope makes LinkedIn reject the authorize request.
  'linkedin-ads': {
    definitionId: 'linkedin-ads',
    authorizeUrl: 'https://www.linkedin.com/oauth/v2/authorization',
    tokenUrl: 'https://www.linkedin.com/oauth/v2/accessToken',
    // ENG-7897: this list is EXACTLY the Advertising API scope set, because
    // Advertising API is the only product added to the Brand Ninja app. Every
    // other scope we used to request here belonged to a product the app does
    // not hold, and LinkedIn rejects the ENTIRE authorize request with
    // `unauthorized_scope_error` if ANY single requested scope is ungranted -
    // so five unapproved scopes bricked the connect for every customer, on
    // every environment, rather than merely disabling five features.
    //
    // Scopes deliberately NOT requested, and the product each needs first:
    //   openid / profile / email        -> Sign In with LinkedIn (OpenID Connect)
    //   r_organization_social           -> Community Management API
    //   r_marketing_leadgen_automation  -> Lead Sync API
    //
    // Dropping the OIDC trio costs only the `Connected as <name>` label: the
    // userInfoUrl fetch in the callback is already best-effort and falls back
    // to a plain 'Connected'. Do NOT re-add any of these until the matching
    // product shows under "Added products" on the app - use the
    // OAUTH_LINKEDIN_ADS_SCOPES override below to widen without a deploy once
    // an approval lands, and only then promote it into this constant.
    defaultScopes: [
      // Advertising API: read + manage campaigns/creatives, and reporting.
      'r_ads',
      'rw_ads',
      'r_ads_reporting',
    ],
    // Read-only variant: reporting only, no campaign/creative mutation. Used
    // when an install is (re)connected with read_only: true. Same product
    // constraint as above - `r_organization_social` was removed with it.
    readOnlyScopes: [
      'r_ads',
      'r_ads_reporting',
    ],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'body',
    userInfoUrl: 'https://api.linkedin.com/v2/userinfo',
  },
};

/**
 * ENG-9815: the PLATFORM'S OWN Xero credential, under a definition id no
 * agent-facing lookup can match.
 *
 * The control-plane row used to live under `definition_id = 'xero'`,
 * distinguished from a customer install only by `config.control_plane_only`.
 * That marker is DISCIPLINARY — every reader has to remember to check it — and
 * two independent readers did not: ENG-9799 exposed it to 15 of 16 agents
 * through the voice mints, and ENG-9814 through the broker's write-capable
 * execution context. Both keyed on `definition_id = 'xero'`, so a distinct id
 * defeats both BY CONSTRUCTION rather than by adding a third reminder.
 *
 * SPREAD, NOT RETYPED. The scope list is the security boundary of the platform's
 * own books; a copy that drifts from Xero's is a silently different grant, and
 * the entire premise here is that it is the same OAuth app.
 *
 * `definitionId` therefore stays `'xero'`, and that is load-bearing rather than
 * an oversight of the spread. It names the OAuth APP this id authenticates as,
 * which is a different question from which install row the id identifies.
 * `getOAuthClientCredentials` and `oauthNotConfiguredError` derive their env
 * keys from it, so this row keeps reading OAUTH_XERO_CLIENT_ID / _SECRET — no
 * new secret, no deploy change, one Xero app as before.
 *
 * WITHOUT THIS ENTRY the control-plane token stops refreshing within ~30
 * minutes, which is the failure the ticket set out to avoid. It named one cause
 * — `oauth-refresh.ts` returns `skipped: no_oauth_provider` for an unknown id.
 * There is a SECOND: `getOAuthClientCredentials` derives `OAUTH_<ID>_CLIENT_ID`
 * from the id too, so an alias that fixed only the provider lookup would have
 * failed one line later on `oauth_client_not_configured` — noisily rather than
 * silently, and just as dead. Both are covered, and both have a test.
 */
OAUTH_PROVIDERS['xero-control-plane'] = {
  ...OAUTH_PROVIDERS['xero']!,
};

/**
 * Split a stored OAuth `granted_scopes` value into individual scope tokens.
 *
 * OAuth servers disagree on the delimiter. RFC 6749 mandates a single
 * space-separated string (Xero, Google, Granola all comply), but **GitHub
 * returns its granted scopes COMMA-separated** — e.g. `repo,read:org,gist,workflow`
 * — in both the token-response `scope` field and the `X-OAuth-Scopes` header.
 * We persist that value verbatim (`credentials.granted_scopes = tokenData.scope`
 * in the OAuth callback), so any consumer that set-diffs the stored value against
 * a provider's `defaultScopes` MUST tolerate both delimiters. A whitespace-only
 * split collapses GitHub's whole comma-joined string into ONE unmatchable token,
 * making every default scope look missing — the bug behind the permanent amber
 * "requires reconnecting" wrench on every GitHub-bound agent, persisting even
 * immediately after a fresh, fully-scoped reconnect (ENG-6237).
 *
 * No OAuth scope token legitimately contains a space or a comma, so splitting on
 * either is safe for every provider. Array elements are split too, in case the
 * storage layer ever proxies a delimited string inside a single-element array.
 */
export function parseGrantedScopes(grantedRaw: unknown): string[] {
  if (typeof grantedRaw === 'string') {
    return grantedRaw.split(/[\s,]+/).filter(Boolean);
  }
  if (Array.isArray(grantedRaw)) {
    return grantedRaw
      .filter((s): s is string => typeof s === 'string')
      .flatMap((s) => s.split(/[\s,]+/))
      .filter(Boolean);
  }
  return [];
}

export function getOAuthProvider(definitionId: string): OAuthProviderConfig | undefined {
  return OAUTH_PROVIDERS[definitionId];
}

export function isOAuthIntegration(definitionId: string): boolean {
  return definitionId in OAUTH_PROVIDERS;
}

/**
 * The OAuth providers that are ALSO remote MCPs, i.e. those carrying `mcpUrl`.
 *
 * ENG-10614 / Obi-Wan `aca77d4ec4aa`. This list exists because the two families
 * of remote MCP are discovered in completely different places, and a consumer
 * that only knows one of them silently covers half the fleet:
 *
 *   - api-key / live-header lanes come from the CATALOG COLUMN
 *     `integration_definitions.remote_mcp` (anchor-browser, monday, peec).
 *   - OAuth lanes are rendered from THIS FILE (granola, brand-ninja, kajabi) and
 *     have no `remote_mcp` column at all.
 *
 * `/host/agent-integrations` read its per-tool HITL floors from the catalog
 * column alone, so the OAuth lane's `toolTiers` was permanently undefined and
 * the parameter threaded into `buildOAuthRemoteMcpProxyEntry` was dead on every
 * host - while `seeds/tool-definitions.json` carried 19 rows for brand-ninja, 4
 * for granola and 34 for kajabi, including `brand-ninja/list_credentials` at
 * `admin`. The gap was not that those integrations had nothing to gate; it was
 * that nothing ever asked.
 */
export function oauthRemoteMcpDefinitionIds(): string[] {
  return Object.entries(OAUTH_PROVIDERS)
    .filter(([, p]) => typeof p.mcpUrl === 'string' && p.mcpUrl.length > 0)
    .map(([id]) => id)
    .sort();
}

/**
 * Environment-variable name carrying a scope override for a provider.
 *
 * Mirrors the existing `OAUTH_<PROVIDER>_CLIENT_ID` / `_SECRET` convention:
 * the definitionId uppercased with `-` → `_`. So `linkedin-ads` reads
 * `OAUTH_LINKEDIN_ADS_SCOPES` and `OAUTH_LINKEDIN_ADS_READONLY_SCOPES`.
 */
export function oauthScopeEnvVar(definitionId: string, readOnly: boolean): string {
  const slug = definitionId.toUpperCase().replace(/-/g, '_');
  return `OAUTH_${slug}_${readOnly ? 'READONLY_SCOPES' : 'SCOPES'}`;
}

/**
 * Read an operator scope override out of the environment.
 *
 * WHY THIS EXISTS (ENG-7897). Which scopes a provider will actually grant is a
 * vendor-side fact that no code constant can track: it depends on which API
 * products the vendor has approved for our app, which changes over time and
 * differs per environment. LinkedIn makes the cost of getting it wrong extreme
 * — it rejects the WHOLE authorize request with `unauthorized_scope_error` if
 * ANY one requested scope is ungranted, so a single stale scope in a hardcoded
 * list takes down every connect for every customer. Before this override, the
 * only remedy was a code change plus a full deploy, on a list that only an
 * operator looking at the vendor console could get right.
 *
 * Accepts space- or comma-delimited values, matching `parseGrantedScopes`:
 *   OAUTH_LINKEDIN_ADS_SCOPES="r_ads rw_ads r_ads_reporting"
 *
 * Reads `process.env` and stays SYNCHRONOUS deliberately: `effectiveProviderScopes`
 * feeds `computeMissingScopes`, which runs on the agents-list and host-runtime hot
 * paths, so going async to reach `getSecret` would ripple through every call site.
 * The API instead lands the SSM value in `process.env` at cold start via the
 * `MANAGED_SECRETS` bootstrap (`packages/api/src/lib/secrets-bootstrap.ts`), which
 * is what makes an SSM-only change take effect with no deploy. A provider whose
 * keys are NOT in that list is `process.env`-only — add it there rather than
 * assuming SSM is consulted.
 *
 * Unset, blank, or whitespace-only is treated as "no override" rather than
 * "request zero scopes" — an empty CI secret is overwhelmingly more likely to
 * be a misconfiguration than a deliberate request for an unscoped token, and
 * silently consenting to nothing would mint tokens that fail every later call
 * with an error pointing nowhere near the cause.
 */
function readScopeOverride(definitionId: string, readOnly: boolean): string[] | null {
  const raw = typeof process !== 'undefined' ? process.env?.[oauthScopeEnvVar(definitionId, readOnly)] : undefined;
  if (typeof raw !== 'string') return null;
  const scopes = parseGrantedScopes(raw);
  return scopes.length > 0 ? scopes : null;
}

/**
 * The scope set we will actually request for a provider, override applied.
 *
 * Use this ANYWHERE the requested scope set matters — not just at consent.
 * Reading `provider.defaultScopes` directly while an override is in force
 * makes the two disagree, and the drift detector then reports the overridden-
 * away scopes as permanently "missing", pinning the amber reconnect wrench on
 * every install of that provider forever (the ENG-6237 failure mode, which no
 * amount of reconnecting can clear because the scopes are never requested).
 */
export function effectiveProviderScopes(
  provider: OAuthProviderConfig,
  opts: { readOnly?: boolean } = {},
): string[] {
  const readOnly = opts.readOnly === true;
  const override = readScopeOverride(provider.definitionId, readOnly);
  if (override) return override;
  return readOnly ? [...(provider.readOnlyScopes ?? [])] : [...provider.defaultScopes];
}

/**
 * Resolve the BASE consent scopes for a provider, honouring an explicit
 * read-only request (ENG-6170) and any operator scope override (ENG-7897).
 *
 * - Default: returns `provider.defaultScopes`.
 * - `readOnly: true`: returns `provider.readOnlyScopes`, or a fail-loud error
 *   when the provider has no read-only set configured (never silently fall back
 *   to the write scopes — that would defeat the point of asking for read-only).
 *
 * An `OAUTH_<PROVIDER>_READONLY_SCOPES` override satisfies the read-only
 * requirement on its own: it is an explicit operator statement of the
 * read-only set, so it is checked BEFORE the not-supported error. The error
 * still fires when neither the config nor the environment supplies one.
 *
 * This deliberately does NOT apply the agent-required-scope union (ENG-4956);
 * that is the /authorize route's responsibility and is intentionally skipped
 * for read-only requests.
 */
export function resolveBaseConsentScopes(
  provider: OAuthProviderConfig,
  opts: { readOnly?: boolean } = {},
): { ok: true; scopes: string[] } | { ok: false; error: string } {
  const scopes = effectiveProviderScopes(provider, opts);
  if (opts.readOnly && scopes.length === 0) {
    return {
      ok: false,
      error: `read_only is not supported for '${provider.definitionId}' (no read-only scope set configured)`,
    };
  }
  return { ok: true, scopes };
}
